import React from 'react';
import styled from 'styled-components';
import {
  useSyncWelcomeInOptIns,
  useWelcomeInOptInSyncMeta,
  useWelcomeInOptIns,
} from '../hooks/useWelcomeInOptIns';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const ActionButton = styled.button`
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  border: 1px solid var(--primaryDark);
  background: var(--accent);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) {
    background: var(--primaryDark);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LastSync = styled.div`
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
`;

const RightCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const RoleBadge = styled.span`
  background: #eef4ff;
  color: var(--primary);
  border-radius: 999px;
  padding: 2px 8px;
  margin-right: 4px;
  font-size: 0.7rem;
  font-weight: 600;
`;

export const WelcomeInOptInPage: React.FC = () => {
  const { data, isLoading, error } = useWelcomeInOptIns();
  const { data: syncMeta } = useWelcomeInOptInSyncMeta();
  const { mutateAsync: sync, isPending: isSyncing } = useSyncWelcomeInOptIns();

  const handleSync = async () => {
    try {
      const result = await sync();
      const messages: string[] = [];
      if (result?.upserted !== undefined) {
        messages.push(`Synced ${result.upserted} Welcome In opt-ins from Clearstream.`);
      }
      if (result?.visitorsAdded !== undefined) {
        messages.push(`Added ${result.visitorsAdded} visitors.`);
      }
      if (result?.visitorHistoryAdded !== undefined) {
        messages.push(`Added ${result.visitorHistoryAdded} visitor status entries.`);
      }
      if (result?.skippedMissingContact) {
        messages.push(`Skipped ${result.skippedMissingContact} rows missing email/phone.`);
      }
      if (result?.skippedCreateUser) {
        messages.push(
          `Skipped ${result.skippedCreateUser} rows due to user creation failures. Check function logs.`
        );
      }
      if (messages.length) {
        window.alert(messages.join('\n'));
      }
    } catch (err) {
      console.error('[SyncWelcomeInOptIns] failed', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      window.alert(
        `Sync failed: ${message}. Check function logs (sync-clearstream) for details.`
      );
    }
  };

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Welcome In - Opt in</HeaderTitle>
        <RightCluster>
          <LastSync>
            Last sync:{' '}
            {syncMeta?.last_synced_at ? formatDateTime(syncMeta.last_synced_at) : 'Not yet synced'}
          </LastSync>
          <ActionButton onClick={handleSync} disabled={isSyncing}>
            {isSyncing ? 'Syncing...' : 'Sync from Clearstream'}
          </ActionButton>
        </RightCluster>
      </HeaderRow>

      {isLoading && <p>Loading users...</p>}
      {error && <p>Error loading users</p>}
      {!isLoading && !error && (
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Phone</th>
                <th>Roles</th>
                <th>Opted In</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((u) => (
                <tr key={u.id}>
                  <td>{getFirstName(u)}</td>
                  <td>{getLastName(u)}</td>
                  <td>{u.phone ?? '-'}</td>
                  <td>
                    {u.roles?.length
                      ? u.roles.map((r) => <RoleBadge key={r}>{r}</RoleBadge>)
                      : 'visitor'}
                  </td>
                  <td>{formatDateTime(u.opted_in_at ?? u.created_at ?? null)}</td>
                </tr>
              ))}
            </tbody>
            </Table>
          </TableWrap>
        </TableCard>
      )}
    </Page>
  );
};

export default WelcomeInOptInPage;

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function getFirstName(user: { first_name?: string | null }) {
  return user.first_name || '-';
}

function getLastName(user: { last_name?: string | null }) {
  return user.last_name || '-';
}
